import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import LazyLoad from "react-lazy-load";

import { Grid, Box, makeStyles, Typography } from "@material-ui/core";

import {
  NALabel,
  IconTitle,
  HighEconomicLabel,
  HighPhysicalLabel,
  ProfitabilityOverTime,
  UnpaidElectricityBills,
  ProfitabilityComponents,
  PowerConsumptionOverTime,
  NoTableData,
} from "../../../../components/parts";
import { Meters } from "./electricity/meters";
import { COLORS } from "../../../../theme/colors";
import { Button } from "../../../../components/base";
import { TenantAlert } from "../tenants/tenantAlerts";
import { TenantProfile } from "./profile/tenantProfile";
import { Documents } from "../units/electricity/documents";
import { TenancyDataDetail } from "./tenancyData/tenancyDataDetail";
import TenancyImg from "../../../../assets/images/icons/tenancy.svg";
import BellImg from "../../../../assets/images/icons/bell_black.svg";
import RevenuesImg from "../../../../assets/images/icons/nav/revenues.svg";
import ProfitabilityImg from "../../../../assets/images/icons/profit.svg";
import AttachementImg from "../../../../assets/images/icons/attachement.svg";
import ElectricityImg from "../../../../assets/images/icons/electricity.svg";
import { TenantTenancyDataInfo } from "./tenancyData/tenantTenancyDataInfo";
import { TenantTenancyDataGraph } from "./tenancyData/tenantTenancyDataGraph";
import ProfileReloadImg from "../../../../assets/images/icons/profile-reload.svg";
import { PowerConsumptionComparison } from "./electricity/powerConsumptionComparison";
import { SupplyRevenuesTotal } from "../properties/electricity/SupplyRevenuesTotal";
import { RevenueBreakdownOverTimeComponent } from "../properties/electricity/RevenueBreakdownOverTimeComponent";
import {
  getData,
  formatDatetime,
  generateProfitabilityData,
  onlyUniqueValuesInObject,
} from "../../../../services";
import { ActionButtonsGroup } from "../../../../components/buttonsGroup/ActionButtonsGroup";
import {
  powerConsumptionOverTimeAreas,
  profitabilityComponentsTableTypeName,
  getProfitabilityComponentsTableHeader,
  profitabilityComponentsTableRowStyle,
} from "../../constants";
import { TenantsRebatePolicyModal } from "../../../../components/modals";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  back: {
    color: COLORS.blue,
    paddingRight: 10,
    cursor: "pointer",
  },
}));

const defaultProfitabilityOverTime = {
  chart_data: [],
  reference_areas: [],
};

const defaultPowerConsumptionOverTime = {
  chart_data: [],
  previous_year_consumption: 0,
};

export const TenantDetail = ({ building, complex, tenant, setTenant, setShowTenantList }) => {
  const classes = useStyles();
  const { id } = useParams();
  const { user } = useSelector((state) => state.auth);
  const currencySymbol = user.currency.symbol;
  const powerConsumptionOverTimeOptions = ["kWh", currencySymbol];
  const url = `/praas/v2/buildings/${id}/tenants/${tenant.tenant.id}`;
  const [tableBody, setTableBody] = useState([]);
  const [unpaidData, setUnpaidData] = useState([]);
  const [powerConsumptionOverTime, setPowerConsumptionOverTime] = useState(
    defaultPowerConsumptionOverTime
  );
  const [powerConsumptionOverTimeOption, setPowerConsumptionOverTimeOption] = useState(
    powerConsumptionOverTimeOptions[0]
  );
  const [profitabilityOverTime, setProfitabilityOverTime] = useState(defaultProfitabilityOverTime);
  const [doc, setDoc] = useState();
  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    getData(`${url}/profitability-components/`, (data) => {
      const results = [];
      for (const [key, value] of Object.entries(data)) {
        results.push({
          type: profitabilityComponentsTableTypeName[key],
          rowStyles: profitabilityComponentsTableRowStyle[key],
          ...value,
        });
      }
      setTableBody(results);
    });

    getData(`${url}/unpaid-bills/`, (data) => {
      if (!onlyUniqueValuesInObject(data, null)) {
        setUnpaidData([{ value: data.electricity_debt }, { value: data.count }]);
      }
    });

    getData(`${url}/profitability-graph/`, (results) => {
      const { chartData, economicXAxisGroups, physicalXAxisGroups, emptyXAxisGroups } =
        generateProfitabilityData(results);

      const referenceAreas = [];
      economicXAxisGroups.forEach((group) => {
        if (group.length >= 2) {
          referenceAreas.push({
            dataKey: "index",
            x1: group[0],
            x2: group[group.length - 1],
            label: <HighEconomicLabel />,
          });
        }
      });
      physicalXAxisGroups.forEach((group) => {
        if (group.length >= 2) {
          referenceAreas.push({
            dataKey: "index",
            x1: group[0],
            x2: group[group.length - 1],
            label: <HighPhysicalLabel />,
          });
        }
      });
      emptyXAxisGroups.forEach((group) => {
        if (group.length > 1) {
          referenceAreas.push({
            dataKey: "index",
            x1: group[0],
            x2: group[group.length - 1],
            label: <NALabel />,
          });
        }
      });

      setProfitabilityOverTime({
        chart_data: chartData,
        reference_areas: referenceAreas,
      });
    });

    getData(`${url}/missing-docs/`, (data) => {
      if (!onlyUniqueValuesInObject(data, null)) setDoc(data);
    });

    return () => {
      setTableBody([]);
      setUnpaidData([]);
      setProfitabilityOverTime(defaultProfitabilityOverTime);
      setDoc();
    };
  }, []);

  useEffect(() => {
    getData(`${url}/consumption-graph/?type=${powerConsumptionOverTimeOption}`, (data) => {
      const chartData = data.consumption.map((consum) => ({
        x: formatDatetime(new Date(consum.date), "YYYY-M"),
        consumption:
          powerConsumptionOverTimeOption === powerConsumptionOverTimeOptions[0]
            ? consum.kwh
            : consum.price,
        unit:
          powerConsumptionOverTimeOption === powerConsumptionOverTimeOptions[0]
            ? "kwh"
            : currencySymbol,
      }));
      setPowerConsumptionOverTime({
        chart_data: chartData,
        previous_year_consumption: data.previous_year_consumption,
      });
    });

    return () => setPowerConsumptionOverTime(defaultPowerConsumptionOverTime);
  }, [powerConsumptionOverTimeOption]);

  return (
    <Box className={classes.root}>
      <Grid
        style={{ marginBottom: 28 }}
        container
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <IconTitle
          iconSrc={TenancyImg}
          title="TENANCY DATA & ANALYTICS"
          rootStyle={{
            marginTop: 0,
            marginBottom: 0,
          }}
        />
        <Grid container alignItems="center" style={{ width: "fit-content" }}>
          <Typography
            className={classes.back}
            onClick={() => {
              setTenant(null);
            }}
            style={{
              paddingRight: 20,
            }}
          >
            Tenants list
          </Typography>
          <Typography
            className={classes.back}
            onClick={() => setShowTenantList(false)}
            style={{ borderRight: `1px solid ${COLORS.darks[0][25]}` }}
          >
            Back to Main
          </Typography>
          <ActionButtonsGroup hideIntegrateBtn />
        </Grid>
      </Grid>
      <TenantTenancyDataInfo tId={tenant.tenant.id} />

      <Grid container spacing={3} style={{ marginTop: 30 }}>
        <Grid item xs={4}>
          <TenancyDataDetail tId={tenant.tenant.id} />
        </Grid>
        <Grid item xs={8}>
          <TenantTenancyDataGraph tId={tenant.tenant.id} />
        </Grid>
      </Grid>

      <IconTitle iconSrc={ElectricityImg} title="ELECTRICITY DATA & ANALYTICS" />

      <Grid container spacing={3} style={{ minHeight: 384 }}>
        <Grid item sm={8} spacing={2}>
          <Meters tId={tenant.tenant.id} />
        </Grid>
        <Grid item sm={4} spacing={3}>
          <Documents buildingId={id} doc={doc} />
        </Grid>
      </Grid>

      <UnpaidElectricityBills type="tenant" data={unpaidData} />

      <Grid container spacing={3} style={{ marginTop: 30, minHeight: 384 }}>
        <Grid item xs={4}>
          <PowerConsumptionOverTime
            title="Tenant Pwr Consumption Over Time"
            chartData={powerConsumptionOverTime.chart_data}
            chartAreas={powerConsumptionOverTimeAreas}
            previousYearConsumption={powerConsumptionOverTime.previous_year_consumption}
            toggleOption={powerConsumptionOverTimeOption}
            toggleOptions={powerConsumptionOverTimeOptions}
            handleChange={setPowerConsumptionOverTimeOption}
          />
        </Grid>
        <Grid item xs={8}>
          <PowerConsumptionComparison tId={tenant.tenant.id} />
        </Grid>
      </Grid>

      {building?.id ? (
        <>
          <Grid
            style={{ paddingTop: 25 }}
            container
            justifyContent="space-between"
            alignItems="center"
          >
            <IconTitle
              iconSrc={RevenuesImg}
              title="PRaaS Electricity Supply Revenues"
              rootStyle={{
                marginTop: 0,
                marginBottom: 0,
              }}
            />
            <Box style={{ marginLeft: "auto", height: "fit-content" }}>
              <Button text="TENANTS REBATE POLICY" onClick={() => setOpen(true)} />
            </Box>
            <TenantsRebatePolicyModal
              isOpen={isOpen}
              setOpen={() => setOpen(false)}
              building={building}
            />
          </Grid>
          <Grid container spacing={3} style={{ marginTop: 30, minHeight: 384 }}>
            <Grid item lg={4}>
              <LazyLoad height="100%">
                <SupplyRevenuesTotal
                  buildingId={building?.id}
                  complexId={complex?.id}
                  title={"Revenues - Total"}
                />
              </LazyLoad>
            </Grid>
            <Grid item lg={8}>
              <LazyLoad height="100%">
                <RevenueBreakdownOverTimeComponent
                  buildingId={building?.id}
                  complexId={complex?.id}
                />
              </LazyLoad>
            </Grid>
          </Grid>
        </>
      ) : null}

      <IconTitle iconSrc={ProfitabilityImg} title="TENANT PROFITABILITY" />

      <ProfitabilityOverTime
        title={`Tenant Revenues Over Time (${currencySymbol})`}
        buttonText="Optimize UNIT Profitability"
        onClick={() => null}
        chartData={profitabilityOverTime.chart_data}
        referenceAreas={profitabilityOverTime.reference_areas}
      />

      <ProfitabilityComponents
        title="Tenant Revenues Components"
        tableHeader={getProfitabilityComponentsTableHeader(user.unit_system)}
        tableBody={tableBody}
      />

      <IconTitle iconSrc={ProfileReloadImg} title="TENANT PROFILE" />

      <TenantProfile />

      <IconTitle iconSrc={BellImg} title="TENANT'S ALERTS" />

      <TenantAlert tId={tenant.tenant.id} />

      <IconTitle iconSrc={AttachementImg} title="UNIT'S ATTACHMENTS" />

      <NoTableData title="No data available" />

      {
        // <Grid container spacing={3}>
        //   <Grid item xs={4}>
        //     <Attachement
        //       icon={PdfImg}
        //       ext="PDF"
        //       name="Document long long name comes here.pdf"
        //       date="July 10, 2020"
        //       size="26.9 Mb"
        //     />
        //   </Grid>
        //   <Grid item xs={4}>
        //     <Attachement
        //       icon={DocImg}
        //       ext="DOC"
        //       name="Document long long name comes here.docx"
        //       date="July 10, 2020"
        //       size="26.9 Mb"
        //     />
        //   </Grid>
        // </Grid>
      }
    </Box>
  );
};
